import {
  Entity, PrimaryGeneratedColumn, Column, CreateDateColumn, UpdateDateColumn,
  ManyToOne, JoinColumn, OneToMany, AfterLoad, AfterInsert, AfterUpdate, getManager, Not, IsNull,
} from 'typeorm';
import { IsOptional, IsInt, IsBoolean } from 'class-validator';
import moment from 'moment-timezone';
import {
  TipoConsulta, Edema, ApresentacaoFetal, PosicaoFetal, MovimentoFetal,
} from '../types';
import { Paciente } from './Paciente';
import { Usuario } from './Usuario';
import { Gestacao } from './Gestacao';
import { ConsultaColetiva } from './ConsultaColetiva';
import { PressaoArterial } from './PressaoArterial';
import { ResultadoDeExame } from './ResultadoDeExame';

@Entity({ name: 'consultas' })
export class Consulta {
  @AfterInsert()
  @AfterUpdate()
  async atualizaDateTimeTriagemGestacao?() {
    if (!this.gestacao_id) {
      return;
    }
    const gestacaoRepo = await getManager().getRepository(Gestacao);
    const consultaRepo = await getManager().getRepository(Consulta);
    let started: Date | null | undefined = null;
    if (!this.datetime_cancelamento && this.tipo_consulta === TipoConsulta.P) {
      started = this.started;
    }
    const consulta = await consultaRepo.findOne({
      where: {
        id: Not(this.id),
        gestacao_id: this.gestacao_id,
        tipo_consulta: TipoConsulta.P,
        datetime_cancelamento: IsNull(),
        started: Not(IsNull()),
      },
      order: {
        started: 'ASC',
      },
    });
    if (consulta) {
      if (started && consulta.started) {
        const diff = moment(started).diff(moment(consulta.started), 's');
        if (diff > 0) {
          started = consulta.started;
        }
      } else {
        started = consulta.started;
      }
    }
    await gestacaoRepo.update({ id: this.gestacao_id }, { datetime_triagem: started });
  }

  @AfterLoad()
  // workaround pra limitações do typeorm
  async carregaPaciente() {
    const paciente: Paciente = await getManager().getRepository(Paciente).findOne({
      loadEagerRelations: false,
      relations: ['gestacoes'],
      where: {
        id: this.paciente_id,
      },
    });
    this.paciente = paciente;
  }

  @PrimaryGeneratedColumn()
  @IsOptional()
  @IsInt()
  id?: number;

  @Column()
  @IsOptional()
  consulta_coletiva_id?: number | null;

  @Column()
  @IsInt()
  paciente_id: number;

  @Column()
  @IsOptional()
  gestacao_id: number | null;

  @Column()
  @IsOptional()
  datetime_agendamento?: Date | null;

  @Column()
  @IsOptional()
  nome_atendente?: string | null;

  @Column()
  @IsOptional()
  datetime_cancelamento?: Date | null;

  @Column()
  @IsOptional()
  justificativa_cancelamento?: string | null;

  @Column()
  @IsBoolean()
  @IsOptional()
  presenca_consulta_coletiva?: boolean;

  @Column({
    type: 'enum',
    enum: TipoConsulta,
  })
  tipo_consulta: TipoConsulta;

  @Column()
  @IsOptional()
  peso_gramas?: number | null;

  @Column()
  @IsOptional()
  altura_uterina?: number | null;

  @Column()
  @IsOptional()
  freq_card_materna?: number | null;

  @Column({
    type: 'enum',
    enum: MovimentoFetal,
  })
  @IsOptional()
  movimento_fetal?: MovimentoFetal | null;

  @Column({
    type: 'enum',
    enum: Edema,
  })
  @IsOptional()
  edema?: Edema | null;

  @Column()
  @IsOptional()
  queixas_condutas?: string | null;

  @Column()
  queixas_condutas_privada?: boolean;

  @Column()
  @IsOptional()
  pedidos_exames?: string | null;

  @Column()
  @IsOptional()
  mamas?: string | null;

  // FETO 1 E 2
  @Column()
  @IsOptional()
  bat_card_fetal1?: number | null;

  @Column({
    type: 'enum',
    enum: ApresentacaoFetal,
  })
  apres_fetal1?: ApresentacaoFetal;

  @Column({
    type: 'enum',
    enum: PosicaoFetal,
  })
  posicao_fetal1?: PosicaoFetal;

  @Column()
  @IsOptional()
  bat_card_fetal2?: number | null;

  @Column({
    type: 'enum',
    enum: ApresentacaoFetal,
  })
  apres_fetal2?: ApresentacaoFetal;

  @Column({
    type: 'enum',
    enum: PosicaoFetal,
  })
  posicao_fetal2?: PosicaoFetal;

  @Column()
  @IsOptional()
  responsavel_coletiva_id?: number | null;

  @Column()
  @IsOptional()
  responsavel_individual_id?: number | null;

  @Column()
  @IsOptional()
  started?: Date | null;

  @Column()
  @IsOptional()
  started_by?: number | null;

  @Column()
  @IsOptional()
  ended?: Date | null;

  @Column()
  @IsOptional()
  ended_by?: number | null;

  @CreateDateColumn()
  @IsOptional()
  created?: Date;

  @Column()
  @IsOptional()
  created_by?: number | null;

  @UpdateDateColumn()
  @IsOptional()
  updated?: Date;

  @Column()
  @IsOptional()
  updated_by?: number | null;

  // RELATIONS
  @ManyToOne(() => ConsultaColetiva)
  @JoinColumn({ name: 'consulta_coletiva_id' })
  consulta_coletiva?: ConsultaColetiva;

  @ManyToOne(() => Paciente, {
    // eager: true,
  })
  @JoinColumn({ name: 'paciente_id' })
  paciente?: Paciente;

  @ManyToOne(() => Gestacao, {
    eager: true,
  })
  @JoinColumn({ name: 'gestacao_id' })
  gestacao?: Gestacao;

  @ManyToOne(() => Usuario)
  @JoinColumn({ name: 'responsavel_coletiva_id' })
  responsavel_coletiva?: Usuario;

  @ManyToOne(() => Usuario)
  @JoinColumn({ name: 'responsavel_individual_id' })
  responsavel_individual?: Usuario;

  @OneToMany(() => PressaoArterial, (pressoes_arteriais) => pressoes_arteriais.consulta, {
    eager: true,
    cascade: ['insert', 'update'],
  })
  pressoes_arteriais?: PressaoArterial[];

  @OneToMany(() => ResultadoDeExame, (resultados_de_exames) => resultados_de_exames.consulta, {
    eager: true,
    cascade: ['insert', 'update'],
  })
  resultados_de_exames?: ResultadoDeExame[];

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'started_by' })
  usuario_started_by?: Usuario;

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'ended_by' })
  usuario_ended_by?: Usuario;

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'created_by' })
  usuario_created_by?: Usuario;

  @ManyToOne(() => Usuario, { eager: true })
  @JoinColumn({ name: 'updated_by' })
  usuario_updated_by?: Usuario;
}
